import {
	columnFilteringFeature,
	columnSizingFeature,
	columnVisibilityFeature,
	createColumnHelper,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	filterFn_arrIncludes,
	filterFn_equals,
	filterFn_inDateRange,
	filterFn_inNumberRange,
	filterFn_includesString,
	filterFn_weakEquals,
	globalFilteringFeature,
	metaHelper,
	rowPaginationFeature,
	rowSelectionFeature,
	rowSortingFeature,
	sortFn_alphanumeric,
	sortFn_datetime,
	sortFn_text,
	tableFeatures,
	useTable,
	type ColumnHelper,
	type ReactTable,
	type RowData,
	type TableOptions,
} from "@tanstack/react-table";

/** Column `meta` recognized by `DataTable.Content`, typed via the `columnMeta` feature slot. */
interface DataTableColumnMeta {
	align?: "left" | "center" | "right";
	headerClassName?: string;
	cellClassName?: string;
}

/*
 * v9 only bundles explicitly registered features. The registered sortFns/filterFns
 * are the complete candidate sets of the "auto" resolution, so default sorting and
 * filtering behave like v8; other built-ins can be passed as functions per column.
 */
const features = {
	columnFilteringFeature,
	columnSizingFeature,
	columnVisibilityFeature,
	globalFilteringFeature,
	rowPaginationFeature,
	rowSelectionFeature,
	rowSortingFeature,
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	filterFns: {
		arrIncludes: filterFn_arrIncludes,
		equals: filterFn_equals,
		inDateRange: filterFn_inDateRange,
		inNumberRange: filterFn_inNumberRange,
		includesString: filterFn_includesString,
		weakEquals: filterFn_weakEquals,
	},
	sortFns: {
		alphanumeric: sortFn_alphanumeric,
		datetime: sortFn_datetime,
		text: sortFn_text,
	},
	columnMeta: metaHelper<DataTableColumnMeta>(),
};

const dataTableFeatures = tableFeatures(features);
/* registering the paginated row model is what turns client pagination on, so it is a separate set */
const clientPaginationFeatures = tableFeatures({...features, paginatedRowModel: createPaginatedRowModel()});

/* One type for both feature sets: the pagination APIs are always there, only the row model is optional. */
type DataTableFeatures = typeof dataTableFeatures & Partial<Pick<typeof clientPaginationFeatures, "paginatedRowModel">>;

type UseDataTableOptions<TData extends RowData> = Omit<TableOptions<DataTableFeatures, TData>, "features"> & {
	enableClientPagination?: boolean;
};

/* Thin Wrapper around useTable propagated with timr defaults */
function useDataTable<TData extends RowData>({
	enableClientPagination = false,
	...options
}: UseDataTableOptions<TData>): ReactTable<DataTableFeatures, TData> {
	return useTable<DataTableFeatures, TData>({
		features: enableClientPagination && !options.manualPagination ? clientPaginationFeatures : dataTableFeatures,
		defaultColumn: {size: undefined},
		enableSortingRemoval: false,
		sortDescFirst: false,
		enableMultiSort: false,
		...options,
	});
}

/** `createColumnHelper` with the data table's feature set already bound. */
function createDataTableColumnHelper<TData extends RowData>(): ColumnHelper<DataTableFeatures, TData> {
	return createColumnHelper<DataTableFeatures, TData>();
}

export {useDataTable, createDataTableColumnHelper, dataTableFeatures};
export type {DataTableColumnMeta, DataTableFeatures, UseDataTableOptions};
